import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { FilterOptionOption, FormatOptionLabelMeta, InputActionMeta } from 'react-select';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { Field, getFormValues, reduxForm } from 'redux-form';

import type { W_Job_FindAllInProgress_VM } from 'ab-api/web/job/findAllInProgress';
import type { W_Job_FindDataForWorkOrderForm_VM_Attachment, W_Job_FindDataForWorkOrderForm_VM_Directory } from 'ab-api/web/job/findDataForWorkOrderForm';

import { isAllowed } from '@acceligentllc/shared/utils/auth';

import { ColorHex } from '@acceligentllc/shared/enums/color';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';
import { WorkRequestStatus } from '@acceligentllc/shared/enums/workRequestStatus';

import type { RootState } from 'af-reducers';

import { debounce } from 'af-utils/actions.util';
import { bemBlock } from 'af-utils/bem.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import { WORK_ORDER_FORM } from 'af-constants/reduxForms';

import { SEARCH_DELAY } from 'ab-common/constants/value';

import { Button } from '@acceligentllc/storybook';

import Select from 'af-fields/SelectField';

import CanceledRibbon from 'af-components/CanceledRibbon';
import LockedValue from 'af-components/LockedValue';
import BadgeCell from 'af-components/Table6/Cells/BadgeCell';

import * as JobActions from 'af-actions/jobs';

import type WorkOrderFM from '../formModel';
import { AttachmentFM, JobFM, WorkOrderDirectoryFM } from '../formModel';

const JOB_PAGE_SIZE = 200;

type JobItem = W_Job_FindAllInProgress_VM['jobs'][0];

type JobFilterItem = {
	filter: string;
	nextPage: Nullable<number>;
	hasMore: boolean;
	version: number;
};

const JobSelect = Select as unknown as new () => typeof Select<JobItem>;

interface OwnProps {
	createNewJob: (jobCode: string) => void;
	resetTimer: () => void;
	setIsJobChanged: () => void;
}

type Props = OwnProps & ConnectedProps<typeof connector> & InjectedFormProps<WorkOrderFM>;

type TreeReducer = {
	directories: W_Job_FindDataForWorkOrderForm_VM_Directory[];
	attachments: W_Job_FindDataForWorkOrderForm_VM_Attachment[];
	nextId: number;
	oldToNewIdMap: { [id: number]: number; };
};

const _mergeDirectories = (
	oldDirectories: WorkOrderDirectoryFM[],
	oldAttachments: AttachmentFM[],
	newDirectories: WorkOrderDirectoryFM[],
	newAttachments: AttachmentFM[]
) => {

	const _getFindKey = (directoriesByIdMap: { [id: number]: WorkOrderDirectoryFM; }) => {
		const _findKey = (dir: WorkOrderDirectoryFM, key: string) => {
			if (!dir.parentId) {
				return key
					? `${dir.name}:${key}`
					: dir.name;
			}
			return _findKey(directoriesByIdMap[dir.parentId], `${key}:${dir.name}`);
		};
		return _findKey;
	};

	// Create a map of { id -> directory } for old directories from form
	const oldDirectoriesByIdMap = oldDirectories.reduce<{ [id: number]: WorkOrderDirectoryFM; }>((_acc, _dir) => {
		_acc[_dir.id] = _dir;
		return _acc;
	}, {});

	// Create a map of { id -> directory } for new directories from new job
	const newDirectoriesByIdMap = newDirectories.reduce<{ [id: number]: WorkOrderDirectoryFM; }>((_acc, _dir) => {
		_acc[_dir.id] = _dir;
		return _acc;
	}, {});

	const oldAttachmentsByIdMap = oldAttachments.reduce<{ [id: number]: AttachmentFM; }>((_acc, _dir) => {
		_acc[_dir.id] = _dir;
		return _acc;
	}, {});

	// Get functions to create a map of old and new directories by name from root to current
	// eq. Job Ehs:DirectoryName1:DirectoryName2
	const _findOldKey = _getFindKey(oldDirectoriesByIdMap);
	const _findNewKey = _getFindKey(newDirectoriesByIdMap);

	// Create the map of keys by path + filters out directories that were copied from old job but not saved, but not root directories
	const oldDirectoriesByKey = oldDirectories.reduce<{ [key: string]: WorkOrderDirectoryFM; }>((_acc, _dir) => {
		// Remove old directories which were copied from Job but not saved except the root ones
		if (_dir.isJobCopy) {
			if (!_dir.parentId) {
				// Also remove the root directory attachments that are copied from Job except the ones that we're added manually
				const _key = _findOldKey(_dir, '');
				const _newAttachmentIds = _dir.attachmentIds.filter((_attId) => oldAttachmentsByIdMap[_attId] && !oldAttachmentsByIdMap[_attId].isJobCopy);
				_acc[_key] = { ..._dir, attachmentIds: _newAttachmentIds };
			}
			return _acc;
		}
		const _key = _findOldKey(_dir, '');
		_acc[_key] = _dir;
		return _acc;
	}, {});

	const newDirectoriesByKey = newDirectories.reduce<{ [key: string]: WorkOrderDirectoryFM; }>((_acc, _dir) => {
		const _key = _findNewKey(_dir, '');
		_acc[_key] = _dir;
		return _acc;
	}, {});

	const filteredNewDirectories = Object.entries(newDirectoriesByKey).reduce<WorkOrderDirectoryFM[]>((_acc, [_key, _dir]) => {
		// Check whether directory already exists with old directories
		// 1. If it doesn't exist add it but also re-map it's parentId
		// 2. If it does exist and has attachments, just add them
		if (!oldDirectoriesByKey[_key]) {
			// If it has a parent and the parent exists in new directories
			if (_dir.parentId && newDirectoriesByIdMap[_dir.parentId]) {
				// Get the parent and calculate it's directory name path like above
				const _parentDir = newDirectoriesByIdMap[_dir.parentId];
				const _parentDirKey = _findNewKey(_parentDir, '');
				// Use the directory path to fetch it from old directories and remap the id
				if (oldDirectoriesByKey[_parentDirKey]) {
					const _newParentId = oldDirectoriesByKey[_parentDirKey].id;
					_acc.push({ ..._dir, parentId: _newParentId });
				} else if (newDirectoriesByKey[_parentDirKey]) {
					const _newParentId = newDirectoriesByKey[_parentDirKey].id;
					_acc.push({ ..._dir, parentId: _newParentId });
				}
			} else {
				_acc.push(_dir);
			}
		} else if (!!_dir.attachmentIds.length) {
			const _newAttachmentIds = _dir.attachmentIds;
			const _oldDirectory = oldDirectoriesByKey[_key];

			oldDirectoriesByKey[_key] = {
				..._oldDirectory,
				attachmentIds: [..._oldDirectory.attachmentIds, ..._newAttachmentIds],
			};
		}
		return _acc;
	}, []);

	// Remove directories and attachments from old job
	const manuallyAddedAttachments = oldAttachments.filter((_att) => !_att.isJobCopy) ?? [];

	return {
		directories: [...(Object.values(oldDirectoriesByKey)), ...filteredNewDirectories],
		attachments: [...manuallyAddedAttachments, ...newAttachments],
	};
};

/** Recursively go through Job directories and attachments and filter out one that should not be copied */
const _recursivelyReduce = (acc: TreeReducer, dir: TreeReducer['directories'][0]) => {
	const { attachments, directories, parentId } = dir;

	// Filter out attachments that should be copied
	const filteredAttachments = attachments.filter((_att) => !!_att.copyToWorkOrderFlag) ?? [];
	// If not root directory and no attachments and no sub-directories return
	if (parentId && !filteredAttachments.length && !directories.length) {
		return acc;
	}

	// re-map parent ids
	acc.directories.push({
		...dir,
		id: acc.nextId,
		parentId: dir.parentId ? acc.oldToNewIdMap[dir.parentId] : null,
		attachments: filteredAttachments,
	});
	// Set negative id since directory does not exist
	acc.oldToNewIdMap[dir.id] = acc.nextId;
	// Set the next negative id
	acc.nextId -= 1;
	if (filteredAttachments.length) {
		acc.attachments.push(...filteredAttachments);

	}

	return directories.reduce(_recursivelyReduce, acc);
};

const _renderJobCodeValue = (job: WorkOrderFM['job']) => {
	const { status, title, jobCode, isInternal } = job;
	const inProgress = status === WorkRequestStatus.SHORT_CIRCUITED;

	return (
		<div className="work-order-upsert__job-info">
			<div className="work-order-upsert__job-title">
				<div className={bemBlock('rt-status-icon', [inProgress ? 'orange' : 'green'])}>
					{inProgress ?
						<span className="icon-in_progress" /> :
						<span className="icon-check" />
					}
				</div>
				<div className="title-name">
					{title}
				</div>
				{isInternal &&
					<div className="internal-badge">
						<BadgeCell
							badgeColor={ColorHex.BLACK}
							colorNegative={false}
							text="INTERNAL"
						/>
					</div>
				}
			</div>
			<div className="work-order-upsert__job-code">
				{jobCode}
			</div>
		</div>
	);
};

const _getOptionValue = (option: JobItem) => option.id;

const _getNewOptionData = (inputValue: string) => {
	return { jobCode: inputValue };
};

const _highlightText = (text: string, highlight: string) => {
	const _lowerText = text?.toLowerCase?.();
	const _lowerHighLight = highlight?.toLowerCase?.();
	const index = _lowerText.indexOf(_lowerHighLight);

	if (!highlight || index === -1) {
		return <span>{text}</span>;
	}
	const _highlightedText = text.slice(index, index + highlight.length);

	return (
		<>
			{text.split(_highlightedText).map((_subtext, _index, _list) => (
				<span key={_index}>
					{_subtext}{_index + 1 !== _list.length && <mark>{_highlightedText}</mark>}
				</span>)
			)}
		</>
	);
};

const _renderJobMenuItem = (option: JobItem, labelMeta: FormatOptionLabelMeta<JobItem>) => {
	const { id, jobCode, isInternal, title, customerCompany, customerFormatted, travelLocationShort } = option;

	if (!id) {
		return (
			<div>
				<span className="work-order-upsert__job-picker-create-label">
					<span className="icon-plus" />
					Create New Job:
				</span>
				<span>{jobCode}</span>
			</div>
		);
	}

	if (labelMeta.selectValue?.[0]?.id === id) {
		return <strong>{jobCode}</strong>;
	}

	return (
		<div className="work-order-upsert__job-picker-option">
			<div>
				<strong>{_highlightText(jobCode, labelMeta.inputValue)}</strong>
				<small><br /></small>
				{!!customerCompany && <small>{_highlightText(customerCompany, labelMeta.inputValue)}</small>}
				{!!title && <small>{(!!customerCompany) && ' |'} {_highlightText(title, labelMeta.inputValue)}</small>}
				{!!customerFormatted &&
					<small>
						{(!!title || !!customerCompany) && ' |'} {_highlightText(customerFormatted, labelMeta.inputValue)}
					</small>
				}
				{!!travelLocationShort &&
					<small>
						{(!!customerFormatted || !!title || !!customerCompany) && ' |'} {_highlightText(travelLocationShort, labelMeta.inputValue)}
					</small>
				}
			</div>
			{isInternal &&
				<BadgeCell
					badgeColor={ColorHex.BLACK}
					colorNegative={false}
					text="INTERNAL"
				/>
			}
		</div>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _jobCodeFilterBy = (option: FilterOptionOption<any>, text: string) => {
	let { jobCode, title, customerCompany, customerFormatted, travelLocationShort } = option.data;
	const { isInternal } = option.data;

	if (!text) {
		return true;
	}

	const searchText = text.toLowerCase();
	customerCompany = customerCompany ?? '';
	jobCode = jobCode?.toLowerCase() ?? '';
	title = title?.toLowerCase() ?? '';
	customerFormatted = customerFormatted?.toLowerCase() ?? '';
	travelLocationShort = travelLocationShort?.toLowerCase() ?? '';
	const jobTypeText = isInternal ? 'internal' : '';

	return jobCode.includes(searchText)
		|| title.includes(searchText)
		|| customerCompany.includes(searchText)
		|| customerFormatted.includes(searchText)
		|| travelLocationShort.includes(searchText)
		|| jobTypeText.includes(searchText);
};

const JobPicker: React.FC<Props> = (props) => {
	const {
		isCreate,
		loadJobs,
		loadJobData,
		job,
		disabled,
		cancellationReason,
		updatedAt,
		updatedBy,
		status,
		createNewJob,
		resetTimer,
		change,
		setIsJobChanged,
		directories,
		attachments,
	} = props;

	const [showDropdown, setShowDropdown] = React.useState(isCreate);
	const [jobs, setJobs] = React.useState<JobItem[]>([]);
	const latestTextFilterRef = React.useRef<Nullable<string>>('');
	const latestSelectedJobIdRef = React.useRef<Nullable<number>>(null);
	const isLoadingMoreRef = React.useRef(false);

	const paginationRef = React.useRef<JobFilterItem>({
		filter: '',
		nextPage: 1,
		hasMore: false,
		version: 0,
	});

	// On mount
	React.useEffect(() => {
		const initialize = async () => {
			const result = await loadJobs({ filterByText: '', pageSize: JOB_PAGE_SIZE, page: 0 });
			setJobs(result.jobs);

			paginationRef.current = {
				filter: '',
				nextPage: result.nextPage,
				hasMore: result.hasMore,
				version: paginationRef.current.version + 1,
			};
		};
		initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onCreateNew = React.useCallback((...args: Parameters<typeof createNewJob>) => {
		latestTextFilterRef.current = null;
		return createNewJob(...args);
	}, [createNewJob]);

	const loadJobsDebounced = React.useMemo(() => debounce(async (textFilter: string) => {
		if (textFilter !== latestTextFilterRef.current) {
			return;
		}

		const result = await loadJobs({ filterByText: textFilter, pageSize: JOB_PAGE_SIZE, page: 0 });

		paginationRef.current = {
			filter: textFilter,
			nextPage: result.nextPage,
			hasMore: result.hasMore,
			version: paginationRef.current.version + 1,
		};
		setJobs(result.jobs);
	}, SEARCH_DELAY), [loadJobs]);

	const onInputChange = React.useCallback((inputValue: string, { action }: InputActionMeta) => {
		if (action === 'input-change') {
			latestTextFilterRef.current = inputValue;
			loadJobsDebounced(inputValue);
			return;
		}

		if ((action === 'input-blur' || action === 'menu-close') && latestTextFilterRef.current) {
			latestTextFilterRef.current = '';
			loadJobsDebounced('');
		}
	}, [loadJobsDebounced]);

	const resetJobsFilter = React.useCallback(() => {
		latestTextFilterRef.current = '';
		loadJobsDebounced('');
	}, [loadJobsDebounced]);

	const changeJob = React.useCallback(() => setShowDropdown(true), []);

	const onValueChange = React.useCallback(async (selectedJob: JobItem) => {
		resetTimer();

		if (!selectedJob) {
			setShowDropdown(true);

			change('job', null);
			change('jobId', null);
			change('crewType', null);
			change('crewTypeId', null);
			change('customCrewType', null);
			change('jobTimezone', null);
			change('attachments', []);
			change('directories', []);

			latestSelectedJobIdRef.current = null;
			latestTextFilterRef.current = '';
			loadJobsDebounced('');

			return;
		}

		latestSelectedJobIdRef.current = selectedJob.id;
		latestTextFilterRef.current = null;

		const jobData = await loadJobData(selectedJob.id);

		if (latestSelectedJobIdRef.current !== selectedJob.id) {
			return;
		}

		const upsertJob = JobFM.toWorkOrderJobRequestModel(jobData);

		if (upsertJob.isInternal) {
			change('crewType', null);
			change('crewTypeId', null);
		} else {
			change('customCrewType', null);
		}

		if (jobData.projectManager) {
			change('projectManagerId', jobData.projectManager.id);
			change('projectManager', jobData.projectManager);
		}

		change('job', upsertJob);
		change('jobId', upsertJob.id);
		change('jobTimezone', jobData.jobTimezone);

		// Get directories and attachments from Job that must be copied
		const {
			directories: wrDirectories,
			attachments: wrAttachments,
		} = jobData.directories.reduce<TreeReducer>(
			_recursivelyReduce,
			{ directories: [], attachments: [], nextId: -1, oldToNewIdMap: {} }
		);

		let newDirectories = WorkOrderDirectoryFM.bulkFromViewModel(wrDirectories);
		let newAttachments = AttachmentFM.bulkFromViewModel(wrAttachments);

		// Case when we are changing job
		if (job && directories && attachments) {
			const {
				directories: _newDirectories,
				attachments: _newAttachments,
			} = _mergeDirectories(directories, attachments, newDirectories, newAttachments);

			newDirectories = _newDirectories;
			newAttachments = _newAttachments;
		}

		change('directories', newDirectories ?? []);
		change('attachments', newAttachments ?? []);

		setShowDropdown(false);

		setIsJobChanged();
	}, [attachments, change, directories, job, loadJobData, loadJobsDebounced, resetTimer, setIsJobChanged]);

	const loadMoreJobs = React.useCallback(async () => {
		const { filter, nextPage, hasMore, version } = paginationRef.current;

		if (!hasMore || isLoadingMoreRef.current || !nextPage) {
			return;
		}

		isLoadingMoreRef.current = true;

		try {
			const result = await loadJobs({ filterByText: filter, pageSize: JOB_PAGE_SIZE, page: nextPage });

			if (paginationRef.current.version !== version) {
				return;
			}

			paginationRef.current = {
				...paginationRef.current,
				nextPage: result.nextPage,
				hasMore: result.hasMore,
			};

			setJobs((prev) => {
				const seen = new Set(prev.map((j) => j.id));
				return [...prev, ...result.jobs.filter((j) => !seen.has(j.id))];
			});
		} finally {
			isLoadingMoreRef.current = false;
		}
	}, [loadJobs]);

	const renderPicker = () => {
		return (
			<Row className="row--padded">
				<Col lg={16}>
					<Field
						allowNew={true}
						component={JobSelect}
						containerClassName="work-order-upsert__job-dropdown-container"
						containerId="work-order__job"
						disableErrorMessage={true}
						filterOption={_jobCodeFilterBy}
						formatOptionLabel={_renderJobMenuItem}
						getNewOptionData={_getNewOptionData}
						getOptionValue={_getOptionValue}
						name="job"
						onClear={resetJobsFilter}
						onCreateNew={onCreateNew}
						onInputChange={onInputChange}
						onMenuScrollToBottom={loadMoreJobs}
						onValueChange={onValueChange}
						options={jobs}
						placeholder="Select Job or Create New"
					/>
				</Col>
			</Row>
		);
	};

	const renderDetails = () => {
		const {
			travelLocationShort,
			office,
			customerCompany,
		} = job;

		return (
			<>
				<div className="work-order-upsert__info work-order-upsert__section">
					<Row className="row--non-padded">
						<Col lg={6}>
							<LockedValue
								label="Job Title / Job ID"
								value={_renderJobCodeValue(job)}
							/>
						</Col>
						<Col lg={4}>
							<LockedValue
								label="Location"
								value={travelLocationShort}
							/>
						</Col>
						<Col lg={4}>
							<LockedValue
								label="Office"
								value={office}
							/>
						</Col>
						<Col lg={4}>
							<LockedValue
								label="Customer"
								value={customerCompany}
							/>
						</Col>
					</Row>
					{!disabled &&
						<Button
							label="Change Job"
							onClick={changeJob}
							style="secondary"
						/>
					}
				</div>
				{(status === WorkOrderStatus.CANCELED && cancellationReason) &&
					<CanceledRibbon
						cancellationReason={cancellationReason}
						updatedAt={updatedAt}
						updatedBy={updatedBy}
					/>
				}
			</>
		);
	};

	const showPicker = !job || showDropdown;

	return showPicker ? renderPicker() : renderDetails();
};

function mapStateToProps(state: RootState) {
	const { workOrder: { order }, user: { companyData, userData } } = state;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const isCreate = !order?.id;
	const {
		job: woJob,
		status,
		cancellationReason,
		updatedAt,
		updatedBy,
		crewTypeId,
		customCrewType,
		directories,
		attachments,
	} = getFormValues(WORK_ORDER_FORM)(state) as WorkOrderFM;

	const hasCreateJobPermission = isAllowed(PagePermissions.COMPANY.JOBS.CREATE, companyData.permissions, companyData.isCompanyAdmin, userData.role);

	return {
		isCreate,
		disabled: status === WorkOrderStatus.CANCELED || status === WorkOrderStatus.LOCKED || order?.isPaused,
		job: woJob,
		hasCreateJobPermission,
		cancellationReason,
		updatedAt,
		updatedBy,
		crewTypeId,
		customCrewType,
		status,
		directories,
		attachments,
	};
}

function mapDispatchToProps() {
	return {
		loadJobs: JobActions.getAllInProgress,
		loadJobData: JobActions.getWorkOrderFormData,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	connector,
	reduxForm<WorkOrderFM>({ form: WORK_ORDER_FORM })
);

export default enhance(JobPicker);
